import { nextTick } from 'vue'
import { gsap } from 'gsap/all'
import { useSegmentConnectors, useSegmentDrawOnScroll } from '~/composables/core'
import { breakpoints } from '~/config'

const ENABLE_SEGMENT_DRAW = false
const SEGMENT_HIDDEN_MAX_WIDTH = 1000
const ENTRY_FIRST_SEGMENT_ORDER = 1
const ENTRY_SECOND_SEGMENT_ORDER = 2
const ENTRY_SEGMENT_RETRY_FRAMES = 120
const ENTRY_SEGMENT_SPEED = 900
const ENTRY_SEGMENT_START_DELAY_MS = 450
const SEGMENT_PROFILE_ATTR = 'data-segment-profile'
const GENERATED_CONNECTOR_ATTR = 'data-generated-segment-connectors'
const CONNECTOR_FROM_ORDER_ATTR = 'data-connector-from-order'
const CONNECTOR_TO_ORDER_ATTR = 'data-connector-to-order'
const ENTRY_SEGMENT_PRELOAD_ATTR = 'data-entry-segment-preload'
const ENTRY_SEGMENT_PRELOAD_VALUE = 'pending'

export function createLandingMotion({ el, loader }) {
	let entrySegmentTimeline = null
	let entrySegmentRetryRafId = null
	let entrySegmentStartTimeoutId = null
	let entryTrackedSegmentPaths = []
	let entryPrimedSegmentPaths = []
	let hasPlayedEntrySegmentAnimation = false
	let hasRequestedEntrySegmentAnimation = false
	let isEntrySegmentAnimationRunning = false

	const resolveSegmentProfile = () => {
		if (window.innerWidth <= breakpoints.xsmall) return 'mobile'
		if (window.innerWidth <= SEGMENT_HIDDEN_MAX_WIDTH) return 'none'
		return 'desktop'
	}

	const isDisplayVisible = (element) => {
		if (!(element instanceof Element)) return false

		const style = window.getComputedStyle(element)
		return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse'
	}

	const resolvePathLength = (path) => {
		if (!(path instanceof SVGPathElement)) return null
		if (!isDisplayVisible(path)) return null

		try {
			const totalLength = path.getTotalLength()
			if (!Number.isFinite(totalLength) || totalLength <= 0) return null
			return totalLength
		} catch {
			return null
		}
	}

	const getRoot = () => (el?.value instanceof HTMLElement ? el.value : null)

	const setEntrySegmentPreloadVisibility = (isPending) => {
		const root = getRoot()
		if (!(root instanceof HTMLElement)) return

		if (isPending) {
			root.setAttribute(ENTRY_SEGMENT_PRELOAD_ATTR, ENTRY_SEGMENT_PRELOAD_VALUE)
			return
		}

		root.removeAttribute(ENTRY_SEGMENT_PRELOAD_ATTR)
	}

	const matchesProfile = (element, activeProfile) => {
		if (!activeProfile) return false

		const rawProfile = `${element.getAttribute(SEGMENT_PROFILE_ATTR) || ''}`.trim().toLowerCase()
		if (!rawProfile) return true
		return rawProfile === activeProfile
	}

	const resolveSegmentPathEntries = (root, order, activeProfile) => {
		const segment = Array.from(root.querySelectorAll(`[data-segment-order="${order}"]`))
			.filter((element) => element instanceof SVGSVGElement)
			.filter((element) => isDisplayVisible(element))
			.filter((element) => matchesProfile(element, activeProfile))[0]

		if (!(segment instanceof SVGSVGElement)) return []

		const segmentPaths = Array.from(segment.querySelectorAll('path'))
			.filter((path) => path instanceof SVGPathElement)
			.map((path) => ({ path, length: resolvePathLength(path) }))
			.filter((entry) => Number.isFinite(entry.length))

		return segmentPaths
	}

	const resolveGeneratedConnectorEntry = (root, fromOrder, toOrder) => {
		const connectorPath = Array.from(root.querySelectorAll(`[${GENERATED_CONNECTOR_ATTR}] path`))
			.filter((path) => path instanceof SVGPathElement)
			.filter((path) => isDisplayVisible(path))
			.find((path) => {
				const pathFromOrder = Number.parseFloat(path.getAttribute(CONNECTOR_FROM_ORDER_ATTR) || '')
				const pathToOrder = Number.parseFloat(path.getAttribute(CONNECTOR_TO_ORDER_ATTR) || '')
				return Number.isFinite(pathFromOrder) && Number.isFinite(pathToOrder) && pathFromOrder === fromOrder && pathToOrder === toOrder
			})

		if (!(connectorPath instanceof SVGPathElement)) return null

		const length = resolvePathLength(connectorPath)
		if (!Number.isFinite(length)) return null

		return { path: connectorPath, length }
	}

	const resolveEntrySegmentPaths = () => {
		const root = getRoot()
		if (!(root instanceof HTMLElement)) return null

		const activeProfile = resolveSegmentProfile()
		if (!activeProfile || activeProfile === 'none') return null

		const firstSegmentEntries = resolveSegmentPathEntries(root, ENTRY_FIRST_SEGMENT_ORDER, activeProfile)
		if (!firstSegmentEntries.length) return null

		const connectorEntry = resolveGeneratedConnectorEntry(root, ENTRY_FIRST_SEGMENT_ORDER, ENTRY_SECOND_SEGMENT_ORDER)
		const secondSegmentEntries = resolveSegmentPathEntries(root, ENTRY_SECOND_SEGMENT_ORDER, activeProfile)
		if (!secondSegmentEntries.length) return null

		const entries = connectorEntry ? [...firstSegmentEntries, connectorEntry, ...secondSegmentEntries] : [...firstSegmentEntries, ...secondSegmentEntries]
		return {
			entries,
			hasConnector: !!connectorEntry,
		}
	}

	const clearEntrySegmentRetry = () => {
		if (!entrySegmentRetryRafId) return
		cancelAnimationFrame(entrySegmentRetryRafId)
		entrySegmentRetryRafId = null
	}

	const clearEntrySegmentStartDelay = () => {
		if (entrySegmentStartTimeoutId === null) return
		window.clearTimeout(entrySegmentStartTimeoutId)
		entrySegmentStartTimeoutId = null
	}

	const clearEntrySegmentAnimation = () => {
		isEntrySegmentAnimationRunning = false
		entrySegmentTimeline?.kill()
		entrySegmentTimeline = null

		const pathsToReset = new Set([...entryTrackedSegmentPaths, ...entryPrimedSegmentPaths])
		if (!pathsToReset.size) return

		pathsToReset.forEach((path) => {
			path.style.removeProperty('stroke-dasharray')
			path.style.removeProperty('stroke-dashoffset')
		})
		entryTrackedSegmentPaths = []
		entryPrimedSegmentPaths = []
	}

	const primeEntrySegments = (entries) => {
		if (!Array.isArray(entries) || !entries.length) return false

		const nextPrimedPathSet = new Set(entries.map((entry) => entry.path).filter((path) => path instanceof SVGPathElement))
		if (!nextPrimedPathSet.size) return false

		entryPrimedSegmentPaths.forEach((path) => {
			if (nextPrimedPathSet.has(path)) return
			path.style.removeProperty('stroke-dasharray')
			path.style.removeProperty('stroke-dashoffset')
		})

		entries.forEach((entry) => {
			const dashValue = `${entry.length} ${entry.length}`
			gsap.set(entry.path, {
				strokeDasharray: dashValue,
				strokeDashoffset: entry.length,
			})
		})

		entryPrimedSegmentPaths = Array.from(nextPrimedPathSet)
		return true
	}

	const animateEntrySegments = (entries) => {
		if (!Array.isArray(entries) || !entries.length) return false

		clearEntrySegmentAnimation()
		isEntrySegmentAnimationRunning = true
		setEntrySegmentPreloadVisibility(false)
		entryTrackedSegmentPaths = entries.map((entry) => entry.path)

		entries.forEach((entry) => {
			const dashValue = `${entry.length} ${entry.length}`
			gsap.set(entry.path, {
				strokeDasharray: dashValue,
				strokeDashoffset: entry.length,
			})
		})
		entryPrimedSegmentPaths = []

		entrySegmentTimeline = gsap.timeline({
			defaults: { ease: 'none' },
			onComplete: () => {
				isEntrySegmentAnimationRunning = false
				entryTrackedSegmentPaths.forEach((path) => {
					path.style.removeProperty('stroke-dasharray')
					path.style.removeProperty('stroke-dashoffset')
				})
				entryTrackedSegmentPaths = []
				entrySegmentTimeline = null
				refreshSegmentConnectors()
			},
		})

		entries.forEach((entry) => {
			entrySegmentTimeline.to(entry.path, {
				strokeDashoffset: 0,
				duration: Math.max(0.2, entry.length / ENTRY_SEGMENT_SPEED),
			})
		})

		return true
	}

	const playEntrySegmentAnimation = (maxFrames = ENTRY_SEGMENT_RETRY_FRAMES) => {
		if (hasPlayedEntrySegmentAnimation) return

		clearEntrySegmentRetry()
		let remainingFrames = Number.isFinite(maxFrames) ? Math.max(0, Math.floor(maxFrames)) : 0

		const run = () => {
			if (hasPlayedEntrySegmentAnimation) {
				entrySegmentRetryRafId = null
				return
			}

			const resolvedEntry = resolveEntrySegmentPaths()
			const drawEntries = resolvedEntry?.entries || []
			drawEntries.length && primeEntrySegments(drawEntries)
			const hasConnector = !!resolvedEntry?.hasConnector
			const shouldAnimateNow = drawEntries.length && (hasConnector || remainingFrames <= 0)

			if (shouldAnimateNow && animateEntrySegments(drawEntries)) {
				hasPlayedEntrySegmentAnimation = true
				entrySegmentRetryRafId = null
				return
			}

			if (remainingFrames <= 0) {
				setEntrySegmentPreloadVisibility(false)
				entrySegmentRetryRafId = null
				return
			}

			remainingFrames -= 1
			refreshSegmentConnectors()
			entrySegmentRetryRafId = requestAnimationFrame(run)
		}

		run()
	}

	const scheduleEntrySegmentAnimation = () => {
		clearEntrySegmentStartDelay()
		const initialEntry = resolveEntrySegmentPaths()
		initialEntry?.entries?.length && primeEntrySegments(initialEntry.entries)

		entrySegmentStartTimeoutId = window.setTimeout(() => {
			entrySegmentStartTimeoutId = null
			playEntrySegmentAnimation()
		}, ENTRY_SEGMENT_START_DELAY_MS)
	}

	const primeCurrentEntrySegments = () => {
		if (hasPlayedEntrySegmentAnimation || isEntrySegmentAnimationRunning) return

		const resolvedEntry = resolveEntrySegmentPaths()
		const drawEntries = resolvedEntry?.entries || []
		if (!drawEntries.length) return

		primeEntrySegments(drawEntries)
	}

	const { refresh: refreshSegmentDraw, destroy: destroySegmentDraw } = useSegmentDrawOnScroll({
		rootRef: el,
		enabledProfiles: ENABLE_SEGMENT_DRAW ? ['desktop'] : [],
		profileResolver: resolveSegmentProfile,
		start: 'top bottom',
		end: 'bottom center',
	})

	const { refresh: refreshSegmentConnectors, destroy: destroySegmentConnectors } = useSegmentConnectors({
		rootRef: el,
		loader: {},
		enabledProfiles: ['desktop', 'mobile'],
		profileResolver: resolveSegmentProfile,
		isRefreshEnabled: () => !isEntrySegmentAnimationRunning,
		onRefreshComplete: () => {
			primeCurrentEntrySegments()
			if (!ENABLE_SEGMENT_DRAW) return
			refreshSegmentDraw()
		},
	})

	const setup = () => {
		nextTick().then(() => {
			refreshSegmentConnectors()
			primeCurrentEntrySegments()
			if (ENABLE_SEGMENT_DRAW) refreshSegmentDraw()

			document.fonts?.ready?.then(() => {
				refreshSegmentConnectors()
				primeCurrentEntrySegments()
				if (ENABLE_SEGMENT_DRAW) refreshSegmentDraw()
			})
		})
	}

	const init = ({ playEntrySegments = false } = {}) => {
		nextTick().then(() => {
			refreshSegmentConnectors()
			primeCurrentEntrySegments()
			if (ENABLE_SEGMENT_DRAW) refreshSegmentDraw()

			requestAnimationFrame(() => {
				refreshSegmentConnectors()
				primeCurrentEntrySegments()
				if (ENABLE_SEGMENT_DRAW) refreshSegmentDraw()
				if (!playEntrySegments) return

				const playEntry = () => scheduleEntrySegmentAnimation()
				const fontsReady = document.fonts?.ready
				if (fontsReady?.then) {
					fontsReady.then(() => requestAnimationFrame(playEntry))
					return
				}

				playEntry()
			})
		})
	}

	const kill = () => {
		setEntrySegmentPreloadVisibility(false)
		clearEntrySegmentStartDelay()
		clearEntrySegmentRetry()
		clearEntrySegmentAnimation()
		destroySegmentConnectors()
		destroySegmentDraw()
	}

	const mount = () => {
		setEntrySegmentPreloadVisibility(true)
		primeCurrentEntrySegments()

		if (!loader) {
			init({ playEntrySegments: true })
			return
		}

		loader.loaded?.then(() => setup())
		loader.transitionReady?.then(() => {
			if (hasRequestedEntrySegmentAnimation || hasPlayedEntrySegmentAnimation) return
			init()
		})
		loader.ready?.then(() => {
			hasRequestedEntrySegmentAnimation = true
			init({ playEntrySegments: true })
		})
	}

	return {
		mount,
		kill,
	}
}
